import {
  Alert,
  Box,
  Button,
  FormControl,
  FormHelperText,
  Typography,
} from '@mui/material';
import type { MRLocationHistory } from '@pn/core/domain/intel/mr-location-history';
import { getSearchResults } from '@pn/core/loaders/search/getSearchResults';
import {
  clearGridSearchResultLayer,
  useHandleSearchResult,
} from '@pn/core/operations/search';
import { useIntelAccess } from '@pn/core/permissions/isIntelAccessDenied';
import { useMapStorage } from '@pn/core/storage';
import { apiMineralRightsLocationHistoryProvider } from '@pn/services/api/intel/apiMineralRightsLocationHistoryProvider';
import { CustomSearchInput } from '@pn/ui/custom-components/CustomSearchInput';
import { isEmpty, isNil } from 'lodash-es';
import React from 'react';
import { LocationHistory } from 'src/ui/components/tools/LocationHistory';
import { UpgradeAccountToAccessMessage } from 'src/ui/components/UpgradeAccountToAccessMessage';

export function MineralRightsLocationHistory() {
  const { isInitialized: isMapInitialized } = useMapStorage();
  const [landId, setLandId] = React.useState('');
  const [history, setHistory] = React.useState<MRLocationHistory[] | undefined>(
    undefined
  );
  const [errorMsg, setErrorMsg] = React.useState('');
  const [isFetching, setIsFetching] = React.useState(false);
  const [searchedLandId, setSearchedLandId] = React.useState('');

  const access = useIntelAccess();
  const isAccessDenied = access('mineralRightsLocationHistory').denied();
  const handleSearchResult = useHandleSearchResult();

  const handleSubmit = async () => {
    if (isEmpty(landId) || isAccessDenied) return;
    const trimmedLandId = landId.trim();
    setSearchedLandId(trimmedLandId);
    setHistory(undefined);
    setErrorMsg('');
    setIsFetching(true);
    try {
      const data =
        await apiMineralRightsLocationHistoryProvider.getMineralRightsLocationHistory(
          trimmedLandId
        );
      setHistory(data);
      if (isMapInitialized) {
        const results = await getSearchResults(trimmedLandId);
        const mrResult = results.find(
          (result) =>
            result.resultType === 'location' && result.label === landId
        );
        if (mrResult) {
          handleSearchResult(mrResult);
        }
      }
    } catch (error) {
      console.error('Error fetching location agreement history:', error);
      setErrorMsg(
        error instanceof Error ? error.message : 'An unexpected error occurred.'
      );
    } finally {
      setIsFetching(false);
    }
  };

  const handleClear = () => {
    setLandId('');
    setHistory(undefined);
    setErrorMsg('');
    setSearchedLandId('');
    if (isMapInitialized) {
      clearGridSearchResultLayer();
    }
  };

  return (
    <Box>
      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: '1fr min-content',
          alignItems: 'start',
          mb: 2,
        }}
      >
        <FormControl>
          <CustomSearchInput
            value={landId}
            onChange={setLandId}
            placeholder="LLD"
            margin="normal"
            disabled={isFetching || isAccessDenied}
            size="small"
            onEnter={handleSubmit}
            onClear={handleClear}
            sx={{ my: 0 }}
          />
          <FormHelperText id="outlined-land-id-helper-text">
            e.g., 31-005-10W4
          </FormHelperText>
        </FormControl>

        <Button
          type="submit"
          variant="contained"
          loading={isFetching}
          onClick={handleSubmit}
          disabled={isFetching || isEmpty(landId) || isAccessDenied}
        >
          Submit
        </Button>
      </Box>
      {errorMsg && <Alert severity="warning">{errorMsg}</Alert>}
      {!isNil(history) && (
        <Box>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            {searchedLandId} Agreement History
          </Typography>
          <LocationHistory history={history} />{' '}
        </Box>
      )}
      {isAccessDenied && <UpgradeAccountToAccessMessage />}
    </Box>
  );
}
